import { PRODUCT_DATE_TIME_POPUP_CLASS_NAME } from "@/components/ProductPage";
import { useCreateReminderMutation } from "@/actions/notification.actions";
import { App, Button, DatePicker, Form, Input, Modal, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CalendarIcon, ClockIcon } from "lucide-react";
import React from "react";

interface CreateReminderModalProps {
  open: boolean;
  onClose: () => void;
  defaultLink?: string;
  defaultTitle?: string;
}

interface FormValues {
  title: string;
  remindAt: Dayjs;
  description?: string;
  link?: string;
}

/** Schedules a personal reminder linked to the current CRM workflow. */
export const CreateReminderModal: React.FC<CreateReminderModalProps> = ({
  open,
  onClose,
  defaultLink,
  defaultTitle,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const createReminderMutation = useCreateReminderMutation();

  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({
        title: defaultTitle || "",
        link: defaultLink,
        remindAt: dayjs().add(15, "minute"),
        description: "",
      });
    } else {
      form.resetFields();
    }
  }, [open, defaultLink, defaultTitle, form]);

  const setPresetTime = (target: Dayjs) => {
    form.setFieldValue("remindAt", target);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createReminderMutation.mutateAsync({
        title: values.title.trim(),
        remindAt: values.remindAt.toISOString(),
        description: values.description?.trim(),
        link: values.link?.trim() || defaultLink || undefined,
      });
      message.success("Reminder scheduled successfully");
      onClose();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.error || "Failed to schedule reminder");
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 text-base font-semibold text-[#131B2A]">
          <ClockIcon className="h-4 w-4 text-(--brand-primary)" />
          Schedule a Reminder
        </div>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Set Reminder"
      confirmLoading={createReminderMutation.isPending}
      destroyOnHidden
      width={480}
      style={{ maxWidth: "calc(100vw - 1.5rem)" }}
      styles={{
        footer: { marginTop: 20 },
      }}
      okButtonProps={{
        className: "!bg-[#131B2A] hover:!bg-[#1E293B]",
      }}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          label="Reminder Title / Task"
          name="title"
          rules={[{ required: true, whitespace: true, message: "Please specify what you need to be reminded of" }]}
        >
          <Input placeholder="e.g. Follow up on Maldives booking payment" maxLength={150} />
        </Form.Item>

        <Form.Item
          label="Reminder Date & Time"
          name="remindAt"
          rules={[{ required: true, message: "Please pick a date and time" }]}
          className="mb-2"
        >
          <DatePicker
            classNames={{ popup: { root: PRODUCT_DATE_TIME_POPUP_CLASS_NAME } }}
            showTime={{ format: "HH:mm" }}
            format="YYYY-MM-DD HH:mm"
            className="w-full"
            placeholder="Select date and time"
            disabledDate={(current) => current && current < dayjs().startOf("day")}
            suffixIcon={<CalendarIcon className="h-4 w-4 text-gray-400" />}
          />
        </Form.Item>

        {/* Quick presets for mobile and fast desktop selection */}
        <div className="flex items-center flex-wrap gap-1.5 mb-4">
          <Typography.Text type="secondary" className="!text-xs mr-1">
            Quick set:
          </Typography.Text>
          <Button
            size="small"
            type="dashed"
            className="!text-[11px] !px-2 !py-0.5"
            onClick={() => setPresetTime(dayjs().add(15, "minute"))}
          >
            +15 mins
          </Button>
          <Button
            size="small"
            type="dashed"
            className="!text-[11px] !px-2 !py-0.5"
            onClick={() => setPresetTime(dayjs().add(1, "hour"))}
          >
            +1 hour
          </Button>
          <Button
            size="small"
            type="dashed"
            className="!text-[11px] !px-2 !py-0.5"
            onClick={() =>
              setPresetTime(
                dayjs().add(1, "day").hour(10).minute(0).second(0)
              )
            }
          >
            Tomorrow 10 AM
          </Button>
        </div>

        <Form.Item label="Additional Notes (Optional)" name="description" className="!mb-6">
          <Input.TextArea
            rows={3}
            placeholder="Add any helpful details or instructions..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Hidden from UI, preserved in backend and database */}
        <Form.Item name="link" hidden>
          <Input type="hidden" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
